'use client';

// How-to-create chooser for a managed agent. Leads with the conversational
// path (chat with Galexia — the flagship UX, driven by the chat's createAgent
// tool) and offers the manual form as a secondary option. Keeps /agents from
// forcing manual creation.

import { useRouter } from 'next/navigation';
import { ChatCircleDots, SlidersHorizontal, ArrowRight, Cards } from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

// Seed the fresh chat auto-sends. The createAgent tool takes it from there.
export const AGENT_SETUP_SEED =
  'I want to create a new managed agent. Interview me: what it should do, how often it runs, its budget, and which tools or connectors it may use — then create it for me.';

export function AgentCreateChooser({
  open,
  onOpenChange,
  onManual,
  onTemplates,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onManual: () => void;
  onTemplates: () => void;
}) {
  const router = useRouter();

  const startChat = () => {
    onOpenChange(false);
    router.push(`/?prompt=${encodeURIComponent(AGENT_SETUP_SEED)}`);
  };

  const startManual = () => {
    onOpenChange(false);
    onManual();
  };

  const startTemplates = () => {
    onOpenChange(false);
    onTemplates();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create an agent</DialogTitle>
          <DialogDescription>
            Tell Galexia what you want an agent to do and it will set the whole
            thing up with you — or fill out the form yourself.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2.5 pt-1">
          {/* Recommended: chat with Galexia */}
          <button
            type="button"
            onClick={startChat}
            className="group flex w-full items-center gap-3.5 rounded-xl border border-violet-500/40 bg-violet-500/[0.07] px-4 py-3.5 text-left transition-colors hover:border-violet-500/70 hover:bg-violet-500/[0.12]"
          >
            <div className="grid size-10 shrink-0 place-items-center rounded-xl bg-violet-500/15">
              <ChatCircleDots weight="duotone" className="size-5 text-violet-400" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold">Create by chatting with Galexia</span>
                <span className="rounded-full bg-violet-500/20 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-violet-300">
                  Recommended
                </span>
              </div>
              <p className="mt-0.5 text-[12px] leading-snug text-muted-foreground">
                Galexia interviews you — purpose, schedule, budget, tools — then
                creates the agent.
              </p>
            </div>
            <ArrowRight
              weight="bold"
              className="size-4 shrink-0 text-violet-400/70 transition-transform group-hover:translate-x-0.5"
            />
          </button>

          {/* Start from a template */}
          <button
            type="button"
            onClick={startTemplates}
            className="group flex w-full items-center gap-3.5 rounded-xl border border-border/60 px-4 py-3.5 text-left transition-colors hover:border-border hover:bg-muted/40"
          >
            <div className="grid size-10 shrink-0 place-items-center rounded-xl bg-muted/60">
              <Cards weight="duotone" className="size-5 text-muted-foreground" />
            </div>
            <div className="min-w-0 flex-1">
              <span className="text-sm font-bold">Start from a template</span>
              <p className="mt-0.5 text-[12px] leading-snug text-muted-foreground">
                Pick a ready-made agent — research digest, topic monitor, weekly
                recap — and tweak it.
              </p>
            </div>
            <ArrowRight
              weight="bold"
              className="size-4 shrink-0 text-muted-foreground/50 transition-transform group-hover:translate-x-0.5"
            />
          </button>

          {/* Manual form */}
          <button
            type="button"
            onClick={startManual}
            className="group flex w-full items-center gap-3.5 rounded-xl border border-border/60 px-4 py-3.5 text-left transition-colors hover:border-border hover:bg-muted/40"
          >
            <div className="grid size-10 shrink-0 place-items-center rounded-xl bg-muted/60">
              <SlidersHorizontal weight="duotone" className="size-5 text-muted-foreground" />
            </div>
            <div className="min-w-0 flex-1">
              <span className="text-sm font-bold">Set up manually</span>
              <p className="mt-0.5 text-[12px] leading-snug text-muted-foreground">
                Fill out the form — name, brief, schedule, and guardrails.
              </p>
            </div>
            <ArrowRight
              weight="bold"
              className="size-4 shrink-0 text-muted-foreground/50 transition-transform group-hover:translate-x-0.5"
            />
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
